import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SimulationConfig, Ball, Vector2D, CollisionEvent, TrailCircle, BlastEvent, EscapeRing, ShapeType, RenderLayer } from './types';
import { resolveBallCollision, resolveWallCollision, getRandomColor, MorphData } from './physicsUtils';
import { AUDIO } from './AudioService';
import { SettingsPanel } from './SettingsPanel';
import { SimulationStats } from './SimulationStats';
import { SimulationHeader } from './SimulationHeader';
import { SimulationFooter } from './SimulationFooter';
import { DEFAULT_CONFIG, STORAGE_KEY } from './constants';
import { drawBlastEvent, drawCollisionEffect } from './BreakAnimations';
import { drawBackground, drawContainer, drawEscapeRings, drawBall } from './SimulationDrawers';
import { CloudService } from './CloudService';
import { CameraService, CAMERA } from './CameraService';
import { EscapeService } from './EscapeService';
import { ShapeUtils } from './ShapeUtils';

const App: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [config, setConfig] = useState<SimulationConfig>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? { ...DEFAULT_CONFIG, ...JSON.parse(saved) } : DEFAULT_CONFIG;
  });

  const configRef = useRef(config);
  useEffect(() => { configRef.current = config; }, [config]);

  // Async Cloud Load
  useEffect(() => {
    const fetchCloudDefault = async () => {
      try {
        const saves = await CloudService.getSaves();
        const defaultSave = saves.find(s => s.isDefault);
        if (defaultSave) {
          console.log("Loaded default save from cloud:", defaultSave.name);
          setConfig(defaultSave.config);
        }
      } catch (e) {
        console.warn("Could not load default save from cloud", e);
      }
    };
    fetchCloudDefault();
  }, []);
  
  useEffect(() => {
    const unlockAudio = () => {
        AUDIO.resume();
        window.removeEventListener('click', unlockAudio);
        window.removeEventListener('keydown', unlockAudio);
        window.removeEventListener('touchstart', unlockAudio);
    };
    window.addEventListener('click', unlockAudio);
    window.addEventListener('keydown', unlockAudio);
    window.addEventListener('touchstart', unlockAudio);
    return () => {
        window.removeEventListener('click', unlockAudio);
        window.removeEventListener('keydown', unlockAudio);
        window.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  const [ballCount, setBallCount] = useState(0);
  const [bounceCount, setBounceCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUI, setShowUI] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isUploadingBallSound, setIsUploadingBallSound] = useState(false);
  
  const ballsRef = useRef<Ball[]>([]);
  const trailCirclesRef = useRef<TrailCircle[]>([]);
  const collisionEventsRef = useRef<CollisionEvent[]>([]);
  const blastEventsRef = useRef<BlastEvent[]>([]);
  const escapeRingsRef = useRef<EscapeRing[]>([]);
  const initialEscapeRingCountRef = useRef<number>(0); 
  const requestRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(Date.now());
  const hueRef = useRef<number>(0);
  const timeScaleRef = useRef<number>(1.0);
  const isRotationStoppedRef = useRef<boolean>(false);
  const shakeRef = useRef<number>(0);
  const counterPopRef = useRef<number>(1.0);
  const timeRemainingRef = useRef<number>(10);
  
  const morphTimerRef = useRef<number>(0); 
  const morphStateRef = useRef({
      active: false,
      from: 'CIRCLE' as ShapeType,
      to: 'CIRCLE' as ShapeType,
      progress: 0
  });
  
  const escapeAccelerationBoostRef = useRef<number>(0);
  
  const containerRadiusRef = useRef<number>(DEFAULT_CONFIG.containerRadius);
  const rhythmicIndexRef = useRef<number>(0); 

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  }, [config]);

  const initSimulation = useCallback(() => {
    const cfg = configRef.current;
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    AUDIO.resetIndex();
    AUDIO.resetSongCursor(); 
    CAMERA.reset(centerX, centerY);
    setBounceCount(0);
    trailCirclesRef.current = [];
    timeScaleRef.current = cfg.initialSpeed || 1.0;
    isRotationStoppedRef.current = false;
    shakeRef.current = 0;
    counterPopRef.current = 1.0;
    timeRemainingRef.current = cfg.escapeTimerStart || 10;
    containerRadiusRef.current = cfg.containerRadius;
    rhythmicIndexRef.current = 0;
    morphTimerRef.current = 0;
    morphStateRef.current = { active: false, from: cfg.containerShape, to: cfg.containerShape, progress: 0 };
    escapeAccelerationBoostRef.current = 0;
    
    const createBall = (overrides: Partial<Ball>): Ball => ({
        id: Math.random().toString(),
        pos: { x: centerX, y: centerY },
        vel: { x: (Math.random()-0.5)*10, y: (Math.random()-0.5)*10 },
        radius: cfg.ballRadius,
        color: getRandomColor(),
        mass: cfg.ballMass,
        angle: 0,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        trail: [],
        style: cfg.ballStyle,
        ...overrides
    });

    if (cfg.mode === 'RHYTHMIC') {
        ballsRef.current = [createBall({
            id: 'rhythm-ball',
            vel: { x: 10, y: 0 },
            color: cfg.ball1Color,
            mass: 1
        })];
    } else if (cfg.mode === 'ESCAPE') {
        escapeRingsRef.current = EscapeService.generateRings(cfg);
        initialEscapeRingCountRef.current = escapeRingsRef.current.length; 
        ballsRef.current = [createBall({
            id: 'escape-ball',
            radius: cfg.ballRadius * 2.5,
            color: cfg.ball1Color,
            vel: { x: (Math.random() - 0.5) * 12, y: (Math.random() - 0.5) * 12 }
        })];
    } else if (cfg.mode === 'SPAWNER') {
      ballsRef.current = Array.from({ length: cfg.initialBalls }).map((_, i) => createBall({
        id: `init-${i}`,
        pos: { x: centerX + (Math.random() - 0.5) * 40, y: centerY + (Math.random() - 0.5) * 40 },
        vel: { x: (Math.random() - 0.5) * 6, y: (Math.random() - 0.5) * 6 },
        color: i === 0 ? cfg.ball1Color : (i === 1 ? cfg.ball2Color : getRandomColor()),
      }));
    } else {
      const startRadius = cfg.drawerReverseMode ? containerRadiusRef.current * 0.8 : cfg.ballRadius;
      ballsRef.current = [createBall({
        id: 'central-ball',
        radius: startRadius,
        color: cfg.ball1Color,
        vel: { x: (Math.random() - 0.5) * 8, y: (Math.random() - 0.5) * 8 }
      })];
    }
    setBallCount(ballsRef.current.length);
    setElapsedTime(0);
    lastTimeRef.current = Date.now();
    collisionEventsRef.current = [];
    blastEventsRef.current = [];
  }, []);

  useEffect(() => { initSimulation(); }, [config.mode, initSimulation]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === 'Space') { 
        e.preventDefault(); 
        if (isPlaying) initSimulation();
        else setIsPlaying(true);
      }
      else if (e.code === 'Backspace') {
        e.preventDefault();
        setIsPlaying(false);
      }
      else if (e.code === 'KeyS' && configRef.current.mode === 'ESCAPE') isRotationStoppedRef.current = !isRotationStoppedRef.current;
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [initSimulation, isPlaying]);

  const playCollisionSound = () => {
    const cfg = configRef.current;
    if (cfg.ballCollisionSoundType === 'SOUND_EFFECT') AUDIO.playSoundEffect(cfg.selectedBallSoundEffect, cfg, cfg.collisionVolume);
    else if (cfg.ballCollisionSoundType === 'CUSTOM') {
      const customSound = cfg.userSounds.find(s => s.id === cfg.selectedUserSoundId);
      if (customSound) {
          const source = customSound.url || customSound.base64;
          if (source) AUDIO.playCustomBuffer(source, cfg.collisionVolume, cfg, false);
      }
      else AUDIO.playNote(cfg.selectedMelody, cfg, 1.0);
    } 
    else if (cfg.ballCollisionSoundType === 'SONG_SLICE') {
        AUDIO.playNextSlice(cfg);
    }
    else AUDIO.playNote(cfg.selectedMelody, cfg, 1.0);
  };

  const playBreakSound = () => {
    const cfg = configRef.current;
    let type = cfg.breakSoundType;
    let effect = cfg.selectedBreakSoundEffect;
    let customId = cfg.selectedBreakUserSoundId;

    if (cfg.mode === 'ESCAPE' && cfg.enableBreakSoundZones && cfg.breakSoundZones.length > 0) {
        const total = initialEscapeRingCountRef.current || cfg.escapeRingCount;
        const current = escapeRingsRef.current.length; 
        const destroyedCount = total - current + 1; 
        let pct = (destroyedCount / total) * 100;
        pct = Math.max(0.1, Math.min(100, pct)); 

        const zones = [...cfg.breakSoundZones].sort((a, b) => a.threshold - b.threshold);
        let zone = zones.find(z => pct <= z.threshold);
        if (!zone && zones.length > 0) zone = zones[zones.length - 1];
        
        if (zone) {
            type = zone.soundType;
            effect = zone.soundEffect;
            customId = zone.customSoundId;
        }
    }

    if (type === 'SOUND_EFFECT') AUDIO.playSoundEffect(effect, cfg, cfg.collisionVolume * 1.5, true);
    else if (type === 'CUSTOM') {
      const customSound = cfg.userSounds.find(s => s.id === customId);
      if (customSound) {
          const source = customSound.url || customSound.base64;
          if (source) AUDIO.playCustomBuffer(source, cfg.collisionVolume * 1.5, cfg, true, true);
      }
      else AUDIO.playSoundEffect(effect, cfg, cfg.collisionVolume * 1.5, true);
    } else AUDIO.playNote(cfg.selectedMelody, cfg, 1.5, true);
  };

  const triggerBlast = (b: {pos: Vector2D, color: string, radius: number}, cmr?: number, gapAngle?: number) => {
    const cfg = configRef.current;
    // Capture current morph state with baseRadius for additive animation
    const currentMorphData = morphStateRef.current.active ? {
        from: morphStateRef.current.from,
        to: morphStateRef.current.to,
        factor: morphStateRef.current.progress,
        baseRadius: cfg.containerRadius
    } : {
        from: cfg.containerShape,
        to: cfg.containerShape,
        factor: 0,
        baseRadius: cfg.containerRadius
    };

    blastEventsRef.current.push({
      id: Math.random().toString(), pos: { ...b.pos }, startTime: Date.now(), duration: cfg.blastDuration,
      maxRadius: cmr || containerRadiusRef.current * 1.5, color: b.color, animationType: cfg.escapeBreakAnimation, intensity: 1.0,
      shape: currentMorphData ? currentMorphData.from : cfg.containerShape, 
      rotation: cfg.containerRotation,
      gapAngle: gapAngle,
      morphData: currentMorphData
    });

    const maxBlasts = cfg.maxActiveBlasts ?? 30; 
    if (blastEventsRef.current.length > maxBlasts) {
        blastEventsRef.current = blastEventsRef.current.slice(blastEventsRef.current.length - maxBlasts);
    }

    playBreakSound();
    if (cfg.mode === 'ESCAPE') shakeRef.current = 15 * cfg.shakeIntensityMultiplier; 
    if (cfg.escapeStopRotationOnHit && cfg.mode === 'ESCAPE') isRotationStoppedRef.current = true;
  };

  const update = useCallback(() => {
    const cfg = configRef.current; 
    
    if (requestRef.current) cancelAnimationFrame(requestRef.current);
    requestRef.current = requestAnimationFrame(update);
    
    if (!isPlaying) {
        draw();
        lastTimeRef.current = Date.now(); 
        return;
    }
    
    const now = Date.now(), deltaTime = now - lastTimeRef.current;
    lastTimeRef.current = now; setElapsedTime(prev => prev + deltaTime);
    
    hueRef.current = (hueRef.current + cfg.hueCycleSpeed) % 360;
    if (shakeRef.current > 0) { shakeRef.current *= cfg.shakeDecayRate; if (shakeRef.current < 0.1) shakeRef.current = 0; }
    if (counterPopRef.current > 1.0) { counterPopRef.current -= cfg.popDecayRate * (deltaTime / 16.67); if (counterPopRef.current < 1.0) counterPopRef.current = 1.0; }
    collisionEventsRef.current = collisionEventsRef.current.filter(e => now - e.startTime < e.duration);
    blastEventsRef.current = blastEventsRef.current.filter(e => now - e.startTime < e.duration);

    const shapes: ShapeType[] = [
      'CIRCLE', 'SQUARE', 'TRIANGLE', 'PENTAGON', 'HEXAGON', 'HEPTAGON', 'OCTAGON', 
      'NONAGON', 'DECAGON', 'STAR_5', 'STAR_6', 'STAR_8', 'HEART', 'DIAMOND', 
      'CROSS', 'CLOVER', 'FLOWER', 'SHIELD', 'INVERTED_TRIANGLE', 'GEM', 'GEAR'
    ];

    if (cfg.shapeMorphEnabled) {
        const availableShapes = (cfg.morphableShapes && cfg.morphableShapes.length > 0) 
            ? cfg.morphableShapes 
            : shapes;

        if (cfg.shapeMorphContinuous) {
            morphStateRef.current.active = true;
            const cycleTime = Math.max(100, cfg.shapeMorphDuration || 1000);
            morphStateRef.current.progress += deltaTime / cycleTime;
            
            if (morphStateRef.current.progress >= 1) {
                morphStateRef.current.progress = 0;
                let currentIndex = availableShapes.indexOf(morphStateRef.current.to);
                if (currentIndex === -1) currentIndex = 0;
                const nextShape = availableShapes[(currentIndex + 1) % availableShapes.length];
                morphStateRef.current.from = morphStateRef.current.to;
                morphStateRef.current.to = nextShape;
                setConfig(prev => ({ ...prev, containerShape: morphStateRef.current.from })); 
            }
        } else {
            morphTimerRef.current += deltaTime;
            const interval = (cfg.shapeMorphSpeed || 1.0) * 2000;
            if (morphTimerRef.current > interval && !morphStateRef.current.active) {
                morphTimerRef.current = 0;
                let currentIndex = availableShapes.indexOf(cfg.containerShape);
                if (currentIndex === -1) currentIndex = 0;
                const nextShape = availableShapes[(currentIndex + 1) % availableShapes.length];
                morphStateRef.current.active = true;
                morphStateRef.current.from = cfg.containerShape;
                morphStateRef.current.to = nextShape;
                morphStateRef.current.progress = 0;
            }
            if (morphStateRef.current.active) {
                morphStateRef.current.progress += deltaTime / (cfg.shapeMorphDuration || 1000);
                if (morphStateRef.current.progress >= 1) {
                    morphStateRef.current.progress = 1;
                    
                    // CRITICAL FIX: Do not disable morph renderer until the config state has actually updated.
                    // This prevents a 1-frame visual snap back to the old shape.
                    if (cfg.containerShape !== morphStateRef.current.to) {
                        setConfig(prev => ({ ...prev, containerShape: morphStateRef.current.to }));
                        // morphStateRef.current.active remains true, drawing the 'to' shape at 100% progress
                    } else {
                        // Config is now synced, safe to disable morph renderer
                        morphStateRef.current.active = false;
                        // morphTimerRef.current is not reset here to allow 'Interval' to mean 'Start-to-Start'
                        // If you want delay *after* morph, reset it here.
                    }
                }
            }
        }
    } else {
        morphStateRef.current.active = false;
        if (morphStateRef.current.from !== cfg.containerShape) {
             morphStateRef.current.from = cfg.containerShape;
             morphStateRef.current.to = cfg.containerShape;
             morphStateRef.current.progress = 0;
        }
    }

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const rotRad = (cfg.containerRotation || 0) * Math.PI / 180;
    
    const morphData: MorphData | undefined = morphStateRef.current.active ? {
        from: morphStateRef.current.from,
        to: morphStateRef.current.to,
        factor: morphStateRef.current.progress,
        baseRadius: cfg.containerRadius // Inject baseRadius for additive logic
    } : {
        from: cfg.containerShape,
        to: cfg.containerShape,
        factor: 0,
        baseRadius: cfg.containerRadius // Ensure consistency even without morph
    };

    if (cfg.mode === 'RHYTHMIC') {
        const b = ballsRef.current[0];
        if (!b) return;
        const substeps = 4;
        const dt = deltaTime / substeps;
        for(let s=0; s<substeps; s++) {
            b.pos.x += b.vel.x * (dt / 16.6); 
            b.pos.y += b.vel.y * (dt / 16.6);
            const relX = b.pos.x - centerX;
            const relY = b.pos.y - centerY;
            const angle = Math.atan2(relY, relX);
            let shapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, morphData.from, morphData.to, morphData.factor, containerRadiusRef.current);
            const dist = Math.sqrt(relX*relX + relY*relY);
            if (dist + b.radius >= shapeR) { 
                AUDIO.playNote(cfg.selectedMelody, cfg, 1.0);
                collisionEventsRef.current.push({
                    id: Math.random().toString(), pos: {...b.pos}, startTime: now, radius: 20, color: b.color, effectType: cfg.collisionEffect,
                    duration: cfg.collisionEffectDuration || 800
                });
                shakeRef.current = 5;
                const duration = AUDIO.getLastNoteDuration(cfg.selectedMelody, cfg);
                const targetTimeSec = (duration * (cfg.rhythmicTimeScale || 1.0)) / 1000;
                const baseRefDuration = 500;
                const ratio = duration / baseRefDuration;
                let newR = cfg.containerRadius * ratio * (cfg.rhythmicCircleScale || 1.0);
                newR = Math.max(30, Math.min(newR, window.innerHeight/2 - 20));
                containerRadiusRef.current = newR;
                const localPos = {
                    x: relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad),
                    y: relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad)
                };
                const localNormal = ShapeUtils.getNormal(localPos, morphData?.from || cfg.containerShape, containerRadiusRef.current, morphData?.to, morphData?.factor);
                const worldNormal = {
                    x: localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad),
                    y: localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad)
                };
                const overlap = (dist + b.radius) - shapeR;
                if(overlap > 0) {
                    b.pos.x -= worldNormal.x * (overlap + 0.1);
                    b.pos.y -= worldNormal.y * (overlap + 0.1);
                }
                const dot = b.vel.x * worldNormal.x + b.vel.y * worldNormal.y;
                let rx = b.vel.x - 2 * dot * worldNormal.x;
                let ry = b.vel.y - 2 * dot * worldNormal.y;
                const mag = Math.sqrt(rx*rx + ry*ry);
                rx /= mag; ry /= mag;
                const travelDist = newR * 1.95; 
                const requiredSpeed = travelDist / (targetTimeSec * 60); 
                b.vel.x = rx * requiredSpeed;
                b.vel.y = ry * requiredSpeed;
            }
        }
        draw();
        return; 
    }

    if (cfg.speedIncreaseMode === 'TIME') timeScaleRef.current += cfg.speedIncreaseRate * (deltaTime / 1000);
    if (cfg.ballGrowthMode === 'TIME') {
        ballsRef.current.forEach(b => { b.radius = Math.min(cfg.ballMaxRadius, b.radius + cfg.ballGrowthRate * (deltaTime / 1000)); });
    }
    if (cfg.containerShrinkMode === 'TIME') {
        const shrink = cfg.containerShrinkRate * (deltaTime / 1000) * 10; 
        containerRadiusRef.current = Math.max(cfg.minContainerRadius, containerRadiusRef.current - shrink);
    }
    
    if (cfg.containerShrinkMode === 'NONE' && Math.abs(containerRadiusRef.current - cfg.containerRadius) > 1 && cfg.mode !== 'ESCAPE') {
       containerRadiusRef.current = cfg.containerRadius;
    }

    // Apply Acceleration Decay
    if (cfg.escapeAccelerationEnabled && escapeAccelerationBoostRef.current > 0) {
        escapeAccelerationBoostRef.current = Math.max(0, escapeAccelerationBoostRef.current - cfg.escapeAccelerationDecay * (deltaTime / 16.67));
    } else if (!cfg.escapeAccelerationEnabled) {
        escapeAccelerationBoostRef.current = 0;
    }

    const currentTS = (timeScaleRef.current * cfg.masterSpeed) + escapeAccelerationBoostRef.current;
    
    if (cfg.mode === 'ESCAPE') {
        const rotationActive = !isRotationStoppedRef.current && cfg.escapeRotationMode !== 'NONE';
        const rings = escapeRingsRef.current;
        for (let idx = 0; idx < rings.length; idx++) {
            const ring = rings[idx];
            if (cfg.escapeShrinkMode && !cfg.escapeStaticMode) {
              const baseShrink = cfg.escapeShrinkRate * currentTS, minRadiusForRing = cfg.escapeMinRadius + idx * cfg.escapeRingSpacing, oldRadius = ring.radius;
              ring.radius = Math.max(minRadiusForRing, ring.radius - baseShrink);
              // Simplified instant center destruction
              if (idx === 0 && ring.radius <= 10.1) {
                  const b = ballsRef.current[0];
                  if (b) {
                      triggerBlast({ pos: { x: centerX, y: centerY }, color: `hsl(${(hueRef.current + ring.hue) % 360}, 100%, 50%)`, radius: 10 }, oldRadius, ring.angle);
                      if (cfg.escapeTimeChallengeMode) timeRemainingRef.current += Math.max(0, cfg.escapeTimeAddition + (cfg.escapeTimeAdditionStep * (cfg.escapeRingCount - escapeRingsRef.current.length)));
                      rings.shift(); setBounceCount(prev => prev + 1); counterPopRef.current = 1.35;
                  }
              }
            }
            if (rotationActive) {
                let dir = (cfg.escapeRotationMode === 'ALTERNATING') ? (idx % 2 === 0 ? 1 : -1) : (cfg.escapeRotationMode === 'COUNTER_CLOCKWISE' ? -1 : 1);
                ring.angle += cfg.escapeRotationSpeed * dir * currentTS * 0.01;
            }
        }
    }
    
    const spawns: {pos: Vector2D, color: string}[] = [];
    const substeps = cfg.physicsSubsteps;

    for (let s = 0; s < substeps; s++) {
      const stepTS = currentTS / substeps;
      
      for (let i = 0; i < ballsRef.current.length; i++) {
        const b = ballsRef.current[i];
        
        if (cfg.ballRotationMode === 'SPIN') {
            b.angle = (b.angle || 0) + (b.rotationSpeed || 0.1) * stepTS;
        } else if (cfg.ballRotationMode === 'VELOCITY') {
            b.angle = Math.atan2(b.vel.y, b.vel.x);
        } else {
            b.angle = 0; 
        }

        if (s === 0) {
          if (!b.trail) b.trail = []; 
          if (cfg.mode === 'TRAIL' || cfg.trailLength > 0) {
             b.trail.push({ ...b.pos }); 
             if (b.trail.length > cfg.trailLength) b.trail.shift();
          }
        }
        
        if (cfg.mode === 'TRAIL') {
            b.trailAccumulator = (b.trailAccumulator || 0) + (deltaTime / substeps) * currentTS; 
            const interval = Math.max(1, cfg.trailInterval); 
            if (b.trailAccumulator >= interval) {
                trailCirclesRef.current.push({ pos: { ...b.pos }, radius: b.radius, hue: (hueRef.current + trailCirclesRef.current.length * 2) % 360 });
                b.trailAccumulator = 0; 
                if (!cfg.trailPersistence && trailCirclesRef.current.length > 3000) trailCirclesRef.current.shift();
            }
        }

        b.vel.y += cfg.gravity * stepTS; b.vel.x *= Math.pow(cfg.airResistance, stepTS); b.vel.y *= Math.pow(cfg.airResistance, stepTS);
        b.pos.x += b.vel.x * stepTS; b.pos.y += b.vel.y * stepTS;
        
        if (cfg.mode === 'ESCAPE') {
            const relX = b.pos.x - centerX;
            const relY = b.pos.y - centerY;
            const angle = Math.atan2(relY, relX);
            const dist = Math.sqrt(relX*relX + relY*relY);
            
            let ringsProcessed = 0;
            const maxBreaksPerFrame = 50; 

            while (escapeRingsRef.current.length > 0 && ringsProcessed < maxBreaksPerFrame) {
                const ring = escapeRingsRef.current[0];
                
                const originalIndex = cfg.escapeRingCount - escapeRingsRef.current.length;
                const isOpposite = originalIndex < (cfg.escapeOppositeGapCount || 0);
                const targetGapSize = isOpposite ? (cfg.escapeOppositeGapSize || 30) : cfg.escapeGapSize;
                let gapWidth = (targetGapSize * Math.PI) / 180;
                if (cfg.escapeGapAlignment === 'FIXED_SIZE') gapWidth = ((targetGapSize * Math.PI / 180) * cfg.containerRadius) / ring.radius;
                const halfGap = gapWidth / 2;

                // SIDE GUARD PHYSICS
                if (cfg.escapeSideGuards && (cfg.containerShape === 'CIRCLE' || !cfg.containerShape)) {
                    const guardLen = cfg.escapeRingSpacing * 1.5; 
                    if (dist < ring.radius && dist > ring.radius - guardLen) {
                        let normAngle = angle;
                        if (normAngle < 0) normAngle += Math.PI * 2;
                        let ringStart = ring.angle % (Math.PI * 2);
                        if (ringStart < 0) ringStart += Math.PI * 2;
                        
                        let diff = Math.abs(normAngle - ringStart);
                        if (diff > Math.PI) diff = 2 * Math.PI - diff;
                        const angularMargin = (b.radius / ring.radius) * 1.2; 
                        
                        // Robust side guard bounce
                        const isCrossBoundary = diff > Math.PI;
                        const isAbove = isCrossBoundary ? (normAngle < ringStart) : (normAngle > ringStart);
                        const sideAngle = ringStart + (isAbove ? halfGap : -halfGap);
                        
                        if (Math.abs(diff - halfGap) < angularMargin) {
                            // Normal to the radial line is (-sin, cos)
                            // We want it to point towards the ball
                            let nx = -Math.sin(sideAngle);
                            let ny = Math.cos(sideAngle);
                            
                            // Check if this normal points towards the ball's position
                            const dotToBall = (b.pos.x - centerX) * nx + (b.pos.y - centerY) * ny;
                            if (dotToBall < 0) {
                                nx = -nx;
                                ny = -ny;
                            }

                            const dotVel = b.vel.x * nx + b.vel.y * ny;
                            
                            // If moving towards the guard (dotVel < 0), reflect
                            if (dotVel < 0) {
                                b.vel.x -= 2 * dotVel * nx;
                                b.vel.y -= 2 * dotVel * ny;
                                b.vel.x *= cfg.restitution;
                                b.vel.y *= cfg.restitution;
                                
                                // Separation push
                                b.pos.x += nx * (b.radius * 0.5);
                                b.pos.y += ny * (b.radius * 0.5);
                                
                                playCollisionSound();
                            }
                        }
                    }
                }

                // Radius Check - Use Additive logic for collision boundary
                let shapeR = ShapeUtils.getInterpolatedRadiusAtAngle(
                    angle - rotRad, 
                    morphData.from, 
                    morphData.to, 
                    morphData.factor, 
                    ring.radius, 
                    morphData.baseRadius // Pass baseRadius to ensure physics matches visual offset
                );

                if (dist + b.radius < shapeR) break;

                // Gap Check
                let normAngle = angle;
                if (normAngle < 0) normAngle += Math.PI * 2;
                
                let ringStart = ring.angle % (Math.PI * 2);
                if (ringStart < 0) ringStart += Math.PI * 2;
                
                let diff = Math.abs(normAngle - ringStart);
                if (diff > Math.PI) diff = 2 * Math.PI - diff;
                
                const inGap = diff < gapWidth / 2;

                if (inGap) {
                    triggerBlast({ pos: { x: centerX, y: centerY }, color: `hsl(${(hueRef.current + ring.hue) % 360}, 100%, 50%)`, radius: b.radius }, ring.radius, ring.angle);
                    if (cfg.escapeTimeChallengeMode) timeRemainingRef.current += Math.max(0, cfg.escapeTimeAddition + (cfg.escapeTimeAdditionStep * (cfg.escapeRingCount - escapeRingsRef.current.length)));
                    
                    if (cfg.escapeAccelerationEnabled) {
                        escapeAccelerationBoostRef.current = Math.min(
                            cfg.escapeAccelerationMax,
                            escapeAccelerationBoostRef.current + cfg.escapeAccelerationMultiplier
                        );
                    }

                    escapeRingsRef.current.shift(); 
                    setBounceCount(prev => prev + 1); 
                    counterPopRef.current = 1.35;
                    ringsProcessed++;
                    
                    if (escapeRingsRef.current.length === 0) {
                        setTimeout(() => initSimulation(), 2000);
                        break;
                    }
                } else {
                    const impact = resolveWallCollision(b, centerX, centerY, ring.radius, cfg, morphData);
                    if (impact > 0.15) { 
                        playCollisionSound(); 
                        shakeRef.current = Math.max(shakeRef.current, 3 * cfg.shakeIntensityMultiplier);
                    }
                    break; 
                }
            }

        } else {
            const isTouching = resolveWallCollision(b, centerX, centerY, containerRadiusRef.current, cfg, morphData);
            
            if (isTouching > 0.15) { 
               playCollisionSound(); 
               if (cfg.speedIncreaseMode === 'COLLISION') timeScaleRef.current += cfg.speedIncreaseRate;
               if (cfg.ballGrowthMode === 'COLLISION') b.radius = Math.min(cfg.ballMaxRadius, b.radius + cfg.ballGrowthRate);
               if (cfg.containerShrinkMode === 'COLLISION') containerRadiusRef.current = Math.max(cfg.minContainerRadius, containerRadiusRef.current - cfg.containerShrinkRate);
               if (cfg.mode === 'DRAWER' || cfg.mode === 'TRAIL') setBounceCount(prev => prev + 1);
               if (cfg.mode === 'DRAWER') {
                  b.hitPoints = b.hitPoints || []; 
                  const angle = Math.atan2(b.pos.y-centerY, b.pos.x-centerX);
                  const currentShape = morphData ? morphData.from : (cfg.containerShape || 'CIRCLE');
                  const nextShape = morphData ? morphData.to : currentShape;
                  const t = morphData ? morphData.factor : 0;
                  const shapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, currentShape, nextShape, t, containerRadiusRef.current);
                  
                  b.hitPoints.push({ x: centerX + Math.cos(angle)*shapeR, y: centerY + Math.sin(angle)*shapeR });
                }
                
                if (cfg.spawnOnWallCollision && cfg.mode === 'SPAWNER' && ballsRef.current.length < cfg.maxBalls && now - (b.lastSpawnTime || 0) > 150) {
                    ballsRef.current.push({
                        id: Math.random().toString(),
                        pos: { ...b.pos },
                        vel: { x: (Math.random()-0.5)*10, y: (Math.random()-0.5)*10 },
                        radius: cfg.ballRadius,
                        color: b.color,
                        mass: cfg.ballMass,
                        angle: 0,
                        lastSpawnTime: now,
                        trail: [],
                        style: cfg.ballStyle
                    });
                    b.lastSpawnTime = now;
                }
                collisionEventsRef.current.push({ id: Math.random().toString(), pos: { ...b.pos }, startTime: now, radius: b.radius * 2.5, color: b.color, effectType: cfg.collisionEffect, duration: cfg.collisionEffectDuration || 800 });
            } else if (cfg.audioSlidingEnabled && isTouching > 0) { 
                AUDIO.playSlidingSound(Math.sqrt(b.vel.x**2 + b.vel.y**2), cfg);
            }
        }
        const sp = Math.sqrt(b.vel.x * b.vel.x + b.vel.y * b.vel.y);
        if (sp > cfg.maxVelocityLimit) { b.vel.x = (b.vel.x / sp) * cfg.maxVelocityLimit; b.vel.y = (b.vel.y / sp) * cfg.maxVelocityLimit; }
        else if (sp < cfg.minVelocityLimit && sp > 0.01) { b.vel.x = (b.vel.x / sp) * cfg.minVelocityLimit; b.vel.y = (b.vel.y / sp) * cfg.minVelocityLimit; }
      }
      
      if (cfg.mode === 'SPAWNER') {
        for (let i = 0; i < ballsRef.current.length; i++) {
          for (let j = i + 1; j < ballsRef.current.length; j++) {
            const impact = resolveBallCollision(ballsRef.current[i], ballsRef.current[j], cfg.restitution);
            if (impact > 0.15) {
              playCollisionSound(); 
              if (cfg.speedIncreaseMode === 'COLLISION') timeScaleRef.current += cfg.speedIncreaseRate;
              if (cfg.spawnOnBallCollision && ballsRef.current.length < cfg.maxBalls) {
                const b1 = ballsRef.current[i], b2 = ballsRef.current[j];
                if (now - (b1.lastSpawnTime || 0) > 150 && now - (b2.lastSpawnTime || 0) > 150) {
                  spawns.push({ pos: { x: (b1.pos.x + b2.pos.x)/2, y: (b1.pos.y + b2.pos.y)/2 }, color: b1.color }); 
                  b1.lastSpawnTime = now; 
                  b2.lastSpawnTime = now;
                }
              }
            }
          }
        }
      }
    }
    if (spawns.length > 0) {
      spawns.forEach(s => {
        if (ballsRef.current.length < cfg.maxBalls) ballsRef.current.push({ 
            id: Math.random().toString(), pos: s.pos, vel: { x: (Math.random()-0.5)*10, y: (Math.random()-0.5)*10 }, 
            radius: cfg.ballRadius, color: getRandomColor(), mass: cfg.ballMass, trail: [], lastTrailTime: now, lastSpawnTime: now, style: cfg.ballStyle, trailAccumulator: 0, angle: 0
        });
      });
      setBallCount(ballsRef.current.length);
    }
    draw();
  }, [isPlaying, initSimulation]); 

  const draw = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const cfg = configRef.current; 
    const centerX = canvas.width / 2, centerY = canvas.height / 2, globalHue = hueRef.current;
    
    const cam = CAMERA.update(canvas.width, canvas.height, ballsRef.current, cfg, shakeRef.current);
    
    // Check if morphing active for draw
    const morphData: MorphData | undefined = morphStateRef.current.active ? {
        from: morphStateRef.current.from,
        to: morphStateRef.current.to,
        factor: morphStateRef.current.progress,
        baseRadius: cfg.containerRadius
    } : {
        from: cfg.containerShape,
        to: cfg.containerShape,
        factor: 0,
        baseRadius: cfg.containerRadius
    };

    drawBackground(ctx, canvas.width, canvas.height, cfg);
    ctx.save(); 
    
    ctx.translate(cam.x, cam.y);
    ctx.scale(cam.scale, cam.scale);
    if (cam.scale !== 1.0) {
        ctx.translate(centerX * (1 - 1/cam.scale), centerY * (1 - 1/cam.scale));
    }

    const rgbStr = `hsl(${globalHue}, 100%, 50%)`;

    // Layer Drawing Logic
    const renderLayers: Record<RenderLayer, () => void> = {
        WALLS: () => {
            drawContainer(ctx, centerX, centerY, cfg, globalHue, containerRadiusRef.current, morphData);
        },
        RINGS: () => {
            if (cfg.mode === 'ESCAPE') drawEscapeRings(ctx, centerX, centerY, escapeRingsRef.current, cfg, globalHue, morphData);
        },
        TRAIL: () => {
            if (cfg.mode === 'TRAIL') trailCirclesRef.current.forEach(tc => {
                ctx.beginPath(); ctx.arc(tc.pos.x, tc.pos.y, tc.radius, 0, Math.PI * 2);
                const useTrailRGB = cfg.rgbLighting && cfg.useTrailRGB;
                ctx.fillStyle = useTrailRGB ? `hsl(${tc.hue}, 100%, 60%)` : (cfg.trailCustomColor !== '#ffffff' ? cfg.trailCustomColor : cfg.ballColor); 
                ctx.globalAlpha = cfg.trailOpacity; ctx.fill();
                
                if (cfg.trailStrokeWidth > 0) {
                    ctx.lineWidth = cfg.trailStrokeWidth;
                    ctx.strokeStyle = cfg.trailStrokeColor;
                    ctx.globalAlpha = Math.min(1, cfg.trailOpacity + 0.2); 
                    ctx.stroke();
                }
            });
            if (cfg.mode === 'DRAWER') ballsRef.current.forEach(b => b.hitPoints?.forEach(pt => {
                ctx.beginPath(); ctx.moveTo(pt.x, pt.y); ctx.lineTo(b.pos.x, b.pos.y);
                ctx.strokeStyle = (cfg.rgbLighting && cfg.useContainerRGB) ? `hsl(${globalHue}, 100%, 50%)` : b.color; ctx.globalAlpha = cfg.drawerLineOpacity; ctx.lineWidth = cfg.drawerLineThickness; ctx.stroke(); 
            }));
            
            if (cfg.trailLength > 0) ballsRef.current.forEach(b => b.trail?.forEach((pos, idx) => {
                const pr = idx / (b.trail?.length || 1); ctx.beginPath(); ctx.arc(pos.x, pos.y, b.radius * (cfg.trailStartScale + (cfg.trailEndScale - cfg.trailStartScale) * pr) * cfg.trailWidthMultiplier, 0, Math.PI * 2);
                const useTrailRGB = cfg.rgbLighting && cfg.useTrailRGB;
                ctx.fillStyle = useTrailRGB ? `hsl(${(globalHue + idx * 4) % 360}, 100%, 50%)` : (cfg.trailCustomColor !== '#ffffff' ? cfg.trailCustomColor : b.color);
                if (cfg.trailGlowAmount > 0) { ctx.shadowBlur = cfg.trailGlowAmount; ctx.shadowColor = ctx.fillStyle as string; }
                ctx.globalAlpha = pr * cfg.trailOpacity; ctx.fill(); ctx.shadowBlur = 0;
            }));
        },
        BALL: () => {
            ballsRef.current.forEach(b => drawBall(ctx, b, cfg, rgbStr));
        },
        FX: () => {
            collisionEventsRef.current.forEach(e => {
                drawCollisionEffect(ctx, e, cfg, globalHue, canvas.width, canvas.height, cam);
            });
        },
        BLAST: () => {
            blastEventsRef.current.forEach(e => drawBlastEvent(ctx, e, cfg, globalHue, canvas.width, canvas.height, cam)); 
        }
    };

    // Execute render layers in configured order
    const order = cfg.renderOrder || ['WALLS', 'RINGS', 'TRAIL', 'BLAST', 'FX', 'BALL'];
    order.forEach(layer => {
        if (renderLayers[layer]) renderLayers[layer]();
    });

    if (cfg.mode === 'ESCAPE' && cfg.ringCounterPosition !== 'NONE') {
      const bFS = cfg.ringCounterFontSize || 180, tFS = cfg.escapeTimerFontSize || 220, pop = counterPopRef.current;
      
      let ringDisplayVal = escapeRingsRef.current.length;
      if (cfg.ringCounterReverse) {
          const total = initialEscapeRingCountRef.current || cfg.escapeRingCount;
          ringDisplayVal = total - ringDisplayVal;
      }
      
      let displayText = ringDisplayVal.toString();
      let scale = bFS * pop;
      if (cfg.escapeTimeChallengeMode) {
          const cR = Math.max(0, timeRemainingRef.current);
          displayText = Math.ceil(cR).toString();
          scale = tFS * pop;
      }

      ctx.save();
      ctx.shadowBlur = 0;
      let tx = centerX, ty = centerY;
      if (cfg.ringCounterPosition === 'TOP_CENTER') ty = centerY - cfg.containerRadius - 100;
      else if (cfg.ringCounterPosition === 'BOTTOM_CENTER') ty = centerY + cfg.containerRadius + 100;
      else if (cfg.ringCounterPosition === 'FOLLOW_BALL' && ballsRef.current.length > 0) {
          tx = ballsRef.current[0].pos.x; 
          ty = ballsRef.current[0].pos.y + cfg.ringCounterOffset;
      }

      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; 
      ctx.font = `${cfg.ringCounterFontWeight} ${scale}px ${cfg.ringCounterFont}`;
      ctx.globalAlpha = cfg.ringCounterOpacity + (pop - 1.0); 

      const rgbColor = `hsl(${globalHue}, 100%, 50%)`;
      const finalFill = cfg.ringCounterUseRGB ? rgbColor : cfg.ringCounterColor;
      const finalStroke = cfg.ringCounterUseRGBBorder ? rgbColor : cfg.ringCounterStrokeColor;

      if (cfg.ringCounterShadowBlur > 0) {
          ctx.shadowBlur = cfg.ringCounterShadowBlur;
          ctx.shadowColor = cfg.ringCounterShadowColor;
      }
      if (cfg.ringCounterStrokeWidth > 0) {
          ctx.lineWidth = cfg.ringCounterStrokeWidth;
          ctx.strokeStyle = finalStroke;
          ctx.strokeText(displayText, tx, ty);
      }
      if (cfg.escapeTimeChallengeMode && timeRemainingRef.current < 3) ctx.fillStyle = '#ef4444';
      else ctx.fillStyle = finalFill;
      ctx.fillText(displayText, tx, ty);

      if (cfg.escapeTimeChallengeMode && cfg.ringCounterStyle === 'CENTER') {
          ctx.font = `900 ${(bFS * 0.3) * pop}px Inter, sans-serif`;
          ctx.fillStyle = finalFill;
          ctx.globalAlpha = cfg.ringCounterOpacity * 0.7;
          ctx.fillText(`${ringDisplayVal} RINGS`, tx, ty + scale * 0.4);
      }
      ctx.restore();
    }
    ctx.restore();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setIsAnalyzing(true); setAnalysisProgress(0);
    try {
      const frequencies = await AUDIO.analyzeFile(file, config.audioCropStart, config.audioCropEnd, (p) => setAnalysisProgress(p));
      setConfig(prev => ({ ...prev, customFrequencies: frequencies, selectedMelody: 'Custom (Uploaded)' }));
    } catch (e) { alert("Failed to analyze audio."); }
    setIsAnalyzing(false); setAnalysisProgress(0);
  };

  const handleBallSoundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setIsUploadingBallSound(true);
    const uploadedUrl = await CloudService.uploadFile(file);
    let base64 = "";
    try {
        const reader = new FileReader();
        base64 = await new Promise((resolve) => {
            reader.onload = (ev) => resolve(ev.target?.result as string);
            reader.readAsDataURL(file);
        });
    } catch(e) { console.error("Base64 conversion failed", e); }
    const def = file.name.substring(0, 20).replace(/\.[^/.]+$/, "");
    const name = prompt("Enter sound name:", def);
    if (name === null) { setIsUploadingBallSound(false); return; }
    setConfig(prev => ({ ...prev, userSounds: [...prev.userSounds, { 
        id: Math.random().toString(36).substring(7), 
        name: name.trim() || def, 
        base64: base64, url: uploadedUrl || undefined 
    }] }));
    setIsUploadingBallSound(false);
  };

  useEffect(() => {
    const resize = () => { if (canvasRef.current) { canvasRef.current.width = window.innerWidth; canvasRef.current.height = window.innerHeight; } };
    window.addEventListener('resize', resize); resize(); requestRef.current = requestAnimationFrame(update);
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(requestRef.current); };
  }, [update]);

  return (
    <div className={`relative w-full h-screen font-sans overflow-hidden transition-colors duration-500 ${config.darkMode ? 'bg-neutral-950 text-white' : 'bg-[#fafaf5] text-neutral-800'}`} style={{ backgroundColor: config.darkMode ? (config.rgbLighting ? '#050505' : (config.outerColor === '#f5f5dc' ? '#0a0a0a' : config.outerColor)) : config.outerColor }}>
      <canvas ref={canvasRef} className="absolute inset-0 cursor-pointer" onClick={() => setIsPlaying(!isPlaying)} />
      <SimulationStats elapsedTime={elapsedTime} timeScale={timeScaleRef.current} darkMode={config.darkMode} />
      <SimulationHeader config={config} currentRgb={`hsl(${hueRef.current}, 100%, 50%)`} ballCount={ballCount} ringsLeft={escapeRingsRef.current.length} bounceCount={bounceCount} isEscape={config.mode === 'ESCAPE'} />
      <SimulationFooter config={config} currentRgb={`hsl(${hueRef.current}, 100%, 50%)`} />
      <SettingsPanel config={config} setConfig={setConfig} showUI={showUI} isPlaying={isPlaying} setIsPlaying={setIsPlaying} onReset={initSimulation} onSave={() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); setSaveStatus('saved'); setTimeout(() => setSaveStatus('idle'), 2000); }} saveStatus={saveStatus} onFileUpload={handleFileUpload} isAnalyzing={isAnalyzing} analysisProgress={analysisProgress} onBallSoundUpload={handleBallSoundUpload} isUploadingBallSound={isUploadingBallSound} />
      <div className="absolute bottom-6 right-6 z-30 flex flex-col gap-3">
         <button onClick={() => setShowUI(!showUI)} className={`w-12 h-12 rounded-full shadow-2xl flex items-center justify-center transition-all pointer-events-auto ${config.darkMode ? 'bg-white text-black' : 'bg-black text-white'}`}>
            <i className={`fa-solid ${showUI ? 'fa-times' : 'fa-gear'}`}></i>
         </button>
      </div>
    </div>
  );
};

export default App;